import { useTranslation } from 'react-i18next';
import FuseScrollbars from '@fuse/core/FuseScrollbars';
import FuseLoading from '@fuse/core/FuseLoading';
import Icon from '@material-ui/core/Icon';
import { Button } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import withReducer from 'app/store/withReducer';
import { useEffect, useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import clsx from 'clsx';
import { useNotifications } from 'app/admin/notification/NotificationProvider';
import NotificationCard from './NotificationCard';
import reducer from './store';
import { closeNotificationPanel, toggleNotificationPanel } from './store/stateSlice';

const useStyles = makeStyles((theme) => ({ root: { backgroundColor: theme.palette.background.default, width: 320 } }));

function NotificationPanel(props) {
    const { t } = useTranslation('common');
    const classes = useStyles();
    const location = useLocation();
    const dispatch = useDispatch();
    const state = useSelector(({ notificationPanel }) => notificationPanel.state);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const { repo } = props;

    const { update, getList, deleteById, deleteAll, markAsRead } = useNotifications();

    useEffect(() => {
        if (!state) return;
        updateList();
    }, [state, update]);

    const updateList = () => {
        setIsLoading(true);
        getList()
            .then((resp) => setResult(resp))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (state) dispatch(closeNotificationPanel());
    }, [location, dispatch]);

    function handleClose() {
        dispatch(closeNotificationPanel());
    }

    function handleDismiss(id) {
        deleteById(id);
    }

    function handleDismissAll() {
        deleteAll();
    }

    function handleRead(id, state) {
        markAsRead(id, state);
    }

    const getSpecificNotification = (item) => {
        const SpecificNotification = repo[item.notificationType];
        if (SpecificNotification) return <SpecificNotification item={item} />;
        return null;
    };

    return (
        <SwipeableDrawer
            classes={{ paper: clsx(classes.root) }}
            open={state}
            anchor="right"
            onClose={(ev) => dispatch(toggleNotificationPanel())}
            onOpen={() => {
                console.log('Drawer is opening!');
            }}
            disableSwipeToOpen
        >
            <IconButton className="m-4 absolute top-0 right-0 z-999" onClick={handleClose}>
                <Icon color="action">close</Icon>
            </IconButton>
            {isLoading ? <FuseLoading /> : null}
            {!isLoading && result ? (
                result.metadata.totalCount > 0 ? (
                    <FuseScrollbars className="p-16">
                        <div className="flex flex-col">
                            <div className="flex flex-col gap-16 pt-36 mb-36">
                                <Typography className="text-28 font-semibold leading-none">{t('notifications')}</Typography>
                                <div className="w-full flex gap-16 h-22">
                                    <Button className="flex-1 bg-gray-200 text-xs" onClick={() => handleDismissAll()}>
                                        {t('delete_all')}
                                    </Button>
                                    <Button className="flex-1 bg-gray-200 text-xs" component={Link} to={() => '/notification/'}>
                                        {t('show_all')}
                                    </Button>
                                </div>
                            </div>
                            {result.data.map((item) => (
                                <NotificationCard
                                    key={item.id}
                                    className="mb-16"
                                    item={item}
                                    onClose={(id) => handleDismiss(id)}
                                    onReadToggle={(id, read) => handleRead(id, !read)}
                                >
                                    {getSpecificNotification(item)}
                                </NotificationCard>
                            ))}
                        </div>
                    </FuseScrollbars>
                ) : (
                    <div className="flex flex-1 items-center justify-center p-16">
                        <Typography className="text-24 text-center" color="textSecondary">
                            {t('emptyNotifications')}
                        </Typography>
                    </div>
                )
            ) : null}
        </SwipeableDrawer>
    );
}

export default withReducer('notificationPanel', reducer)(memo(NotificationPanel));